<template>
  <div class="cfg-chart-card">
    <div class="cfg-chart-header">
      <h3 :title="widget.title">{{ widget.title || 'Chart' }}</h3>
    </div>
    <div class="cfg-chart-body">
      <div v-if="!hasData" class="cfg-chart-empty">
        <i class="fa fa-chart-bar" aria-hidden="true"></i>
        <span>No data to display</span>
      </div>
      <canvas v-show="hasData" ref="canvas"></canvas>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import _ from 'lodash'
import { buildChartConfig, dataLabelsPlugin } from './utils/chartConfig'
import { toPlainObject } from './utils/plainObject'

// Register the inline data-labels plugin once at module load
let pluginRegistered = false
function ensurePluginRegistered() {
  if (pluginRegistered) return
  try {
    Chart.plugins.register(dataLabelsPlugin)
    pluginRegistered = true
  } catch (e) {
    // Older/newer runtime: ignore plugin registration failures
  }
}

export default {
  name: 'DashboardChart',
  props: {
    widget: { type: Object, required: true },
    data: { type: Array, default: () => [] },
    index: { type: Number, default: 0 },
    theme: { type: Object, required: true },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      chart: null,
      resizeObserver: null,
    }
  },
  watch: {
    data: {
      deep: false,
      handler() {
        if (typeof this.scheduleUpdate === 'function') this.scheduleUpdate()
      },
    },
    widgetConfigKey() { this.rebuild() },
    themeKey() { this.rebuild() },
    compact() { this.rebuild() },
  },
  computed: {
    hasData() { return this.data && this.data.length > 0 },
    widgetConfigKey() {
      return JSON.stringify(toPlainObject(this.widget))
    },
    themeKey() {
      const t = this.theme || {}
      return [t.bg, t.surface, t.text, t.textMuted, t.border, t.accent].join('|')
    },
  },
  mounted() {
    ensurePluginRegistered()
    this.scheduleUpdate = _.debounce(() => this.updateChart(), 60)
    this.rebuild()
    // Observe parent resize to keep canvas fit
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(_.debounce(() => {
        if (this.chart) {
          try { this.chart.resize() } catch (e) { /* ignore */ }
        }
      }, 100))
      this.resizeObserver.observe(this.$el)
    }
  },
  beforeDestroy() {
    this.destroyChart()
    if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null }
  },
  methods: {
    destroyChart() {
      if (this.chart) {
        try { this.chart.destroy() } catch (e) { /* ignore */ }
        this.chart = null
      }
    },
    plainWidget() {
      return toPlainObject(this.widget)
    },
    rebuild() {
      this.destroyChart()
      if (!this.hasData) return
      const canvas = this.$refs.canvas
      if (!canvas) return
      const cfg = buildChartConfig(
        this.data,
        this.plainWidget(),
        this.index,
        this.theme,
        this.compact,
      )
      if (!cfg) return
      const ctx = canvas.getContext('2d')
      this.chart = new Chart(ctx, {
        type: cfg.chartType,
        data: cfg.data,
        options: cfg.options,
      })
    },
    updateChart() {
      if (!this.chart) return this.rebuild()
      if (!this.hasData) { this.destroyChart(); return }
      const cfg = buildChartConfig(
        this.data,
        this.plainWidget(),
        this.index,
        this.theme,
        this.compact,
      )
      if (!cfg) return
      // Chart type changed — must rebuild
      if (this.chart.config.type !== cfg.chartType) {
        this.destroyChart()
        this.rebuild()
        return
      }
      this.chart.data.labels = cfg.data.labels
      // Reconcile datasets in place to avoid animation flicker
      const incoming = cfg.data.datasets
      while (this.chart.data.datasets.length > incoming.length) {
        this.chart.data.datasets.pop()
      }
      incoming.forEach((ds, di) => {
        if (this.chart.data.datasets[di]) {
          Object.assign(this.chart.data.datasets[di], ds)
        } else {
          this.chart.data.datasets.push(ds)
        }
      })
      // Avoid replacing options — Chart.js lodash merge can timeout on large trees.
      this.chart.update()
    },
  },
}
</script>

<style scoped>
.cfg-chart-card {
  background: var(--dash-surface);
  border-radius: 12px;
  border: 1px solid var(--dash-border);
  display: flex; flex-direction: column;
  min-width: 0; height: 100%;
  overflow: hidden;
  transition: background 0.3s ease, transform .15s ease, box-shadow .15s ease;
}
.cfg-chart-card:hover {
  box-shadow: 0 18px 32px -18px var(--dash-shadow);
}
.cfg-chart-header {
  padding: 14px 18px 6px 18px;
  flex-shrink: 0;
}
.cfg-chart-header h3 {
  margin: 0; font-size: .95rem; font-weight: 600; color: var(--dash-text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cfg-chart-body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  width: 100%;
  padding: 0;
  overflow: hidden;
}
.cfg-chart-body canvas {
  position: absolute;
  top: 4px; right: 10px; bottom: 12px; left: 10px;
  width: calc(100% - 20px) !important;
  height: calc(100% - 16px) !important;
  max-width: 100%;
}
.cfg-chart-empty {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: var(--dash-text-muted); font-size: .85rem;
}
.cfg-chart-empty i { font-size: 1.8rem; opacity: .5; }
</style>
